import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { workflowSchedulesService } from '../../api/workflowSchedulesService';
import type { WorkflowSchedule } from '../../api/workflowSchedulesService';

function formatDate(dateString: string | null): string {
  if (!dateString) return '\u2014';
  return new Date(dateString).toLocaleString();
}

export function WorkflowSchedulesListPage() {
  const navigate = useNavigate();

  const listPage = useAdminListPage<WorkflowSchedule, string>({
    queryKey: ['admin', 'workflows', 'schedules'],
    queryFn: () => workflowSchedulesService.list(),
    deleteFn: (id) => workflowSchedulesService.remove(id),
    getDeleteId: (schedule) => schedule.id,
  });

  return (
    <AdminListPage<WorkflowSchedule>
      title="Workflow Schedules"
      subtitle="Manage scheduled workflow executions"
      loadingLabel="Loading workflow schedules..."
      errorTitle="Failed to load workflow schedules"
      errorFallback="An unexpected error occurred while loading workflow schedules."
      emptyTitle="No workflow schedules found"
      emptyDescription="Get started by creating your first workflow schedule."
      createLabel="Create Schedule"
      onCreateClick={() => navigate('/admin/workflows/schedules/create')}
      items={listPage.items}
      getRowKey={(schedule) => schedule.id}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Schedule"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(schedule) => schedule.name}
      deleteNoun="schedule"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      renderHead={() => (
        <>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Cron Expression</Table.HeaderCell>
          <Table.HeaderCell>Timezone</Table.HeaderCell>
          <Table.HeaderCell align="center">Status</Table.HeaderCell>
          <Table.HeaderCell>Next Run</Table.HeaderCell>
          <Table.HeaderCell>Last Triggered</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(schedule) => (
        <>
          <Table.Cell>
            <VStack gap={0}>
              <Text weight="medium">{schedule.name}</Text>
              <Text size="sm" color="muted">
                {schedule.definition?.name ?? '\u2014'}
              </Text>
            </VStack>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" style={{ fontFamily: 'monospace' }}>
              {schedule.cron_expression}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm">{schedule.timezone}</Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={schedule.is_active ? 'success' : 'default'}
              size="sm"
            >
              {schedule.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {formatDate(schedule.next_run_at)}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {schedule.last_triggered_at
                ? formatDate(schedule.last_triggered_at)
                : 'Never'}
            </Text>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${schedule.name}`}
                leftIcon={<Icon icon={Pencil} size="sm" />}
                onClick={() =>
                  navigate(`/admin/workflows/schedules/${schedule.id}/edit`)
                }
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${schedule.name}`}
                leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                onClick={() => listPage.setDeleteTarget(schedule)}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
